import { DataTablePagination, Loader, PanelInfo } from '@/components/custom';
import { confirm } from '@/components/custom/react-confirm/Confirm';
import { useRequestDeleteUserImage } from '@/hooks/httpRequest';
import { useInvalidateQueries, useListenEvents } from '@/hooks';
import BodyPageCertifications from './BodyPageCertifications';
import { useUrlParams, useVerifications } from '../../hooks';
import { notify, notifyInternalMessage } from '@/functions';
import { VerificationTypesEnum } from '@/lib/enum';
import { IUserPicture } from '@/lib/interface';
import BodyPagePhotos from './BodyPagePhotos';
import { useParams } from 'react-router-dom';
import { Podcast, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { useState } from 'react';

export interface IState {
	picturesDeleted: {
		position: string;
		userId: string;
		path: string;
	}[]
}

const BodyPage = () => {

	const { verifications, isLoadingVerifications, data, refresh } = useVerifications();
	const [state, setState] = useState<IState>({ picturesDeleted: [] });
	const { triggerDeleteUserImage } = useRequestDeleteUserImage();
	const { invalidate } = useInvalidateQueries();
	const { type, authorIds } = useUrlParams();
	const { type: typeParam } = useParams();

	useListenEvents({ type: 'userCoinsEdited', callback: _ => invalidate() });
	useListenEvents({ type: 'userDeleted', callback: _ => invalidate() });
	useListenEvents({ type: 'userEdited', callback: _ => invalidate() });
	useListenEvents({ type: 'userWarned', callback: _ => invalidate() });
	useListenEvents({ type: 'userBanned', callback: _ => invalidate() });

	let verificationsCleaned = (
		[...verifications]
			.map(
				verification => {
					const picturesDeleted = state.picturesDeleted.filter(l => l.userId === verification.userId);

					if (picturesDeleted.length !== 0) {
						verification
							.userData
							.pictures = (
								verification
									.userData
									.pictures
									.filter(p => !picturesDeleted.some(pD => pD.path == p.path && pD.position == p.position))
							);
					}

					return verification;
				}
			)
	);

	if (authorIds && authorIds.length !== 0) {
		verificationsCleaned = [...verificationsCleaned].filter(l => authorIds.includes(l.reviewerData?._id));
	}

	const handleOnDeletePicture = async (userId: string, picture: IUserPicture) => {

		const yes = await confirm('Etes-vous sur de vouloir supprimer cette photo du profil de cet utilisateur?');

		if (!yes) {
			return;
		}

		triggerDeleteUserImage({ userId, position: picture.position })
			.then(
				() => {
					// Update state
					setState(
						l => ({
							...l,
							picturesDeleted: [
								...l.picturesDeleted,
								({
									position: picture.position,
									path: picture.path,
									userId
								})
							]
						})
					);
					// Notify deletion
					notify('La photo a été supprimée avec succès.');
				}
			)
			.catch(notifyInternalMessage);
	};

	if (isLoadingVerifications) {
		return (
			<div className='my-10'>
				<Loader className='my-10' />
			</div>
		);
	}

	if (verificationsCleaned.length === 0) {
		return (
			<PanelInfo
				title='Aucune verification trouvée'
				description={`Vous n'avez plus effectué de vérification respectant ces critères.`}
				className='py-10 md:py-26 border-none'
				icon={Podcast}>
				<Button
					variant={'secondary'}
					onClick={refresh}
					type='button'>
					Actualiser la page
				</Button>
			</PanelInfo>
		);
	}

	let content = null;

	if (type == VerificationTypesEnum.IDENTITY) {
		content = <BodyPageCertifications verifications={verificationsCleaned} />;
	} else if (type == VerificationTypesEnum.PROFILE_PHOTO) {
		content = (
			<BodyPagePhotos
				verifications={verificationsCleaned}
				onDeletePicture={handleOnDeletePicture} />
		);
	}

	if (content) {
		return (
			<div className='flex flex-col space-y-5'>
				{content}
				<DataTablePagination pageCount={data.nbrPages} />
			</div>
		)
	}

	return (
		<PanelInfo
			title='Paramètre invalide'
			description={
				<p className='flex space-x-1'>
					<span>
						Aucune intégration n'est prévu pour le paramètre
					</span>
					<span className='text-purple-600'>{typeParam?.toLowerCase()}</span>.
				</p>
			}
			className='py-10 md:py-26 border-none'
			icon={X} />
	);

};

export default BodyPage;
